import React, { useCallback } from 'react';
import { TreeNode } from '../types';
import { 
  ChevronRight, 
  ChevronDown, 
  File, 
  Folder, 
  FolderOpen, 
  Bookmark, 
  BookmarkCheck,
  FileText,
  Image,
  FileCode,
  Archive,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileAudio,
  FileType,
  FileJson,
  Table,
  MessageSquare
} from 'lucide-react';
import './TreeItem.css';

// Helper function to check if a file is a utility file (main XML files or index-md5.txt)
function isUtilityFile(fileName: string): boolean {
  const lowerName = fileName.toLowerCase();
  return lowerName === 'index.xml' || 
         lowerName === 'regional.xml' || 
         lowerName.includes('index-md5.txt');
}

interface TreeItemProps {
  node: TreeNode;
  level: number;
  expandedNodes: Set<string>;
  onToggle: (nodeId: string) => void;
  onDocumentClick: (node: TreeNode) => void;
  onBookmarkToggle: (node: TreeNode, path: string[]) => void;
  isBookmarked: (node: TreeNode) => boolean;
  searchQuery?: string;
  nodePath?: string; // Unique path identifier for this node
  originalTree?: TreeNode[]; // Original unfiltered tree for building stable paths
  getOpenCommentCount?: (node: TreeNode) => number; // Open review comments on this document
}

export function TreeItem({
  node,
  level,
  expandedNodes,
  onToggle,
  onDocumentClick,
  onBookmarkToggle,
  isBookmarked,
  searchQuery = '',
  nodePath = node.name, // Default to node.name for backward compatibility
  originalTree,
  getOpenCommentCount,
}: TreeItemProps) {
  const openCommentCount = node.type === 'file' && getOpenCommentCount ? getOpenCommentCount(node) : 0;
  // Use unique path identifier instead of just node.name
  const uniqueId = nodePath;
  const isExpanded = expandedNodes.has(uniqueId);
  const isBookmarkedNode = isBookmarked(node);

  // Name-based child keys; a 4-digit sequence folder is transparent so the same
  // folder gets the same key in per-sequence and cumulative views (keeps the
  // expansion state intact when switching views). eCTD folder names are unique
  // within a directory, so name paths are unambiguous.
  const buildStableChildPath = useCallback((childNode: TreeNode, parentPath: string): string => {
    const isSequenceFolder = node.type === 'folder' && /^\d{4}$/.test(node.name);
    const childBase = isSequenceFolder
      ? parentPath.split('/').slice(0, -1).join('/')
      : parentPath;
    return childBase ? `${childBase}/${childNode.name}` : childNode.name;
  }, [node]);

  const handleClick = useCallback(() => {
    if (node.type === 'file') {
      onDocumentClick(node);
    } else {
      onToggle(uniqueId);
    }
  }, [node, onDocumentClick, onToggle, uniqueId]);

  const handleBookmarkToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onBookmarkToggle(node, [node.name]); // Simplified path for now
  }, [node, onBookmarkToggle]);

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf':
        return <FileText className="file-icon pdf-icon" />;
      case 'doc':
      case 'docx':
        return <FileText className="file-icon word-icon" />;
      case 'xls':
      case 'xlsx':
        return <FileSpreadsheet className="file-icon excel-icon" />;
      case 'ppt':
      case 'pptx':
        return <FileImage className="file-icon powerpoint-icon" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'bmp':
      case 'tiff':
        return <Image className="file-icon image-icon" />;
      case 'xml':
        return <FileCode className="file-icon xml-icon" />;
      case 'html':
      case 'htm':
        return <FileCode className="file-icon html-icon" />;
      case 'css':
        return <FileCode className="file-icon css-icon" />;
      case 'js':
      case 'jsx':
        return <FileCode className="file-icon js-icon" />;
      case 'ts':
      case 'tsx':
        return <FileCode className="file-icon ts-icon" />;
      case 'json':
        return <FileJson className="file-icon json-icon" />;
      case 'csv':
        return <Table className="file-icon csv-icon" />;
      case 'txt':
      case 'log':
        return <FileType className="file-icon text-icon" />;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
        return <FileVideo className="file-icon video-icon" />;
      case 'mp3':
      case 'wav':
      case 'flac':
        return <FileAudio className="file-icon audio-icon" />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <Archive className="file-icon archive-icon" />;
      default:
        return <File className="file-icon generic-icon" />;
    }
  };

  const getFolderIcon = () => {
    return isExpanded ? 
      <FolderOpen className="folder-icon" /> : 
      <Folder className="folder-icon" />;
  };

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="highlight">{part}</mark>
      ) : part
    );
  };

  const indentStyle = {
    paddingLeft: `${level * 8 + 2}px`,
  };

  let displayName = node.name;
  let stfPrefix: string | null = null;
  if (node.type === 'file') {
    const baseTitle = node.xmlTitle || node.name;
    displayName = baseTitle;

    if (node.isStf) {
      const prefixParts: string[] = [];
      const version = node.stfVersion?.trim();
      if (version) {
        if (/^stf/i.test(version)) {
          prefixParts.push(version);
        } else {
          prefixParts.push(`STF ${version}`);
        }
      } else {
        prefixParts.push('STF');
      }

      const studyId = node.stfStudyId?.trim();
      if (studyId) {
        prefixParts.push(studyId);
      }

      const prefix = prefixParts.join(' - ');
      stfPrefix = `[${prefix}]`;
    }
  }

  const folderSecondaryTitle =
    node.type === 'folder' &&
    node.xmlTitle &&
    node.xmlTitle !== node.name
      ? node.xmlTitle
      : null;

  const metadataEntries =
    node.type === 'folder' && node.metadata
      ? Object.entries(node.metadata)
      : [];

  return (
    <div className="tree-item" style={indentStyle}>
      <div 
        className={`tree-item-content ${node.type} ${isExpanded ? 'expanded' : ''}`}
        onClick={handleClick}
      >
        {node.type === 'folder' && (
          <div className="toggle-icon-container">
            {isExpanded ? 
              <ChevronDown className="toggle-icon" /> : 
              <ChevronRight className="toggle-icon" />
            }
          </div>
        )}

        <div className="item-icon">
          {node.type === 'folder' ? getFolderIcon() : getFileIcon(node.originalName || node.name)}
        </div>

        <div className="item-content">
          <div className="item-name">
            {node.type === 'file' && (
              <span className="sequence-inline">[{node.sequenceId}]</span>
            )}
            <span className="item-title">
              {stfPrefix && (
                <span className="stf-prefix">
                  {highlightText(stfPrefix, searchQuery)}
                  {' '}
                </span>
              )}
              {highlightText(displayName, searchQuery)}
            </span>
            {folderSecondaryTitle && (
              <span className="folder-secondary-title">
                {folderSecondaryTitle}
              </span>
            )}
            {node.type === 'file' && node.lifecycleOp.toLowerCase() === 'replace' && node.replacesSequence && (
              <span className="replace-sequence-badge">
                replaces {node.replacesSequence}
              </span>
            )}
            {metadataEntries.map(([key, value]) => (
              <span key={`${key}-${value}`} className="metadata-badge">
                ({key}="{value}")
              </span>
            ))}
            {node.type === 'file' && !isUtilityFile(node.name) && (
              <span className={`lifecycle-badge ${node.lifecycleOp.toLowerCase()}`}>
                {node.lifecycleOp}
              </span>
            )}
            {openCommentCount > 0 && (
              <span
                className="comment-indicator"
                title={`${openCommentCount} open comment${openCommentCount === 1 ? '' : 's'}`}
              >
                <MessageSquare className="comment-indicator-icon" />
                {openCommentCount}
              </span>
            )}
          </div>
        </div>

        <button
          className={`bookmark-button ${isBookmarkedNode ? 'bookmarked' : ''}`}
          onClick={handleBookmarkToggle}
          aria-label={isBookmarkedNode ? 'Remove bookmark' : 'Add bookmark'}
        >
          {isBookmarkedNode ? 
            <BookmarkCheck className="bookmark-icon" /> : 
            <Bookmark className="bookmark-icon" />
          }
        </button>
      </div>

      {node.type === 'folder' && isExpanded && node.children && (
        <div className="tree-children">
          {node.children.map((child, index) => {
            // Build stable path based on original tree structure
            const childPath = buildStableChildPath(child, nodePath);
            return (
              <TreeItem
                key={`${child.type}-${childPath}-${index}`}
                node={child}
                level={level + 1}
                expandedNodes={expandedNodes}
                onToggle={onToggle}
                onDocumentClick={onDocumentClick}
                onBookmarkToggle={onBookmarkToggle}
                isBookmarked={isBookmarked}
                searchQuery={searchQuery}
                nodePath={childPath}
                originalTree={originalTree}
                getOpenCommentCount={getOpenCommentCount}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
